import React, { useEffect, useMemo } from 'react'
import {
  Background,
  MarkerType,
  ReactFlow,
  ReactFlowProvider,
  useEdgesState,
  useNodesState,
  BackgroundVariant,
} from 'reactflow'
import AudienceNode from '../../ReactFlow/Audiences/AudienceNode'
import SourceNode from '../../ReactFlow/Audiences/SourceNode'
import DestinationNodeAccount from '../../ReactFlow/Audiences/DestinationNodeAccount'
import * as R from 'ramda'
import create from 'zustand'
import { VENDOR } from '../../../lib/utils/vendors'
import DestinationNodeCampaign from '../../ReactFlow/Audiences/DestinationNodeCampaign'
import { get } from 'lodash'
const defaultEdgeOptions = {
  style: { strokeWidth: 3, stroke: 'black' },
  type: 'default',
  animated: true,
  markerEnd: {
    type: MarkerType.ArrowClosed,
    color: 'black',
  },
}

type AudienceFlowDiagramState = {
  nodes: any[]
  setNodes: (nodes: any[]) => void
  selectedNode?: Record<string, any>
  setSelectedNode: (selectedNode?: Record<string, any>) => void
  setSelectedNodeByProperty: (property: string, value: any) => void
}

export const useAudienceFlowDiagramState = create<AudienceFlowDiagramState>(
  (set, getState) => ({
    nodes: [],
    setNodes: (nodes) => set({ nodes }),
    setSelectedNodeByProperty: (property: string, value: any) => {
      const nodes = getState().nodes
      const selectedNode = nodes.find((node) => get(node, property, null) === value)
      set({ selectedNode })
    },
    selectedNode: {
      id: 'this-audience',
      data: {
        name: 'This Audience',
      },
    },
    setSelectedNode: (selectedNode) => {
      set({ selectedNode })
    },
  }),
)

const AudienceFlowDiagram = ({
  connectors,
  isLoading,
}: {
  connectors: any
  isLoading: boolean
}) => {
  const { setNodes: setPersistentNodesState } = useAudienceFlowDiagramState()
  const [nodes, setNodes, onNodesChange] = useNodesState([])
  const [edges, setEdges, onEdgesChange] = useEdgesState([])

  const inputConnectionChunks = useMemo(
    () => R.splitEvery(5, connectors?.inputs ?? []),
    [connectors],
  )
  const outputConnectionChunks = useMemo(
    () => R.splitEvery(5, connectors?.outputs ?? []),
    [connectors],
  )

  useEffect(() => {
    if (!isLoading) {
      const inputNodes = inputConnectionChunks
        .map((chunk, index) => {
          return chunk.map((connector: any, inputIndex, arr) => {
            return {
              id: String(connector.id),
              type: 'source',
              data: {
                name: connector.customer_source,
              },
              position: {
                x: -(connector.customer_source.length * 10) - (inputIndex + 1) * 75,
                y:
                  index < arr.length / 2
                    ? -index * inputConnectionChunks.length * 100
                    : (index * inputConnectionChunks.length - arr.length / 2) * 100,
              },
            }
          })
        })
        .flat(2)
      const campaigns = outputConnectionChunks
        .map((chunk) => {
          return chunk.map((connector: any) => {
            return connector.campaigns.map((campaign: any) => ({
              ...campaign,
              account_id_ref: String(connector._id),
              vendor_account_id:
                connector?.vendor_audience_details?.vendor_account_id ??
                connector?.vendor_audience_details?.advertiser_id,
            }))
          })
        })
        .flat(2)
        .map((campaign, index, arr) => {
          return {
            id: String(campaign.vendor_campaign_id),
            type: 'destination_campaign',
            data: {
              name: campaign.vendor_campaign_name,
              account_id_ref: campaign.account_id_ref,
              vendor_account_id: campaign.vendor_account_id,
              vendor_campaign_id: campaign.vendor_campaign_id,
            },
            position: {
              x: 500 + outputConnectionChunks.length * 100,
              y: index < arr.length / 2 ? -index * 100 : (index - arr.length / 2) * 100,
            },
          }
        })
      const outputNodes = outputConnectionChunks
        .map((chunk, index) => {
          return chunk.map((connector: any, outputIndex) => {
            return {
              id: String(connector._id),
              type: 'destination_account',
              data: {
                name: VENDOR[connector.vendor_id],
                vendor_account_id:
                  connector?.vendor_audience_details?.vendor_account_id ??
                  connector?.vendor_audience_details?.advertiser_id,
                vendor_id: connector.vendor_id,
                vendor_audience_id:
                  connector?.vendor_audience_details?.vendor_audience_id,
              },
              position: {
                x: 300 + outputConnectionChunks.length * index * 75,
                y: outputIndex * 100,
              },
            }
          })
        })
        .flat(2)

      const averageInputNodeY =
        inputNodes.reduce((acc, node) => acc + node.position.y, 0) / inputNodes.length ||
        0
      const _nodes = [
        ...inputNodes,
        {
          id: 'this-audience',
          type: 'audience',
          data: {
            name: 'This audience',
            job: 'Designer',
            emoji: '🤓',
            index: 1,
            variant: 'audience',
          },
          position: {
            x: 0,
            y: averageInputNodeY,
          },
        },
        ...outputNodes,
        ...campaigns,
      ]
      setNodes(_nodes)
      setPersistentNodesState(_nodes)
      const inputEdges = inputConnectionChunks.flat(2).map((connector: any, index) => {
        return {
          id: `e1-${index}`,
          source: String(connector.id),
          target: 'this-audience',
          animated: false,
        }
      })
      const outputEdges = outputConnectionChunks.flat(2).map((connector: any, index) => {
        return {
          id: `e2-${index}`,
          source: 'this-audience',
          target: String(connector._id),
          animated: true,
        }
      })
      const campaignEdges = campaigns.map((campaign, index) => {
        return {
          id: `e3-${index}`,
          source: String(campaign.data.account_id_ref),
          target: String(campaign.id),
          animated: true,
        }
      })
      setEdges([...inputEdges, ...outputEdges, ...campaignEdges])
    }
  }, [
    isLoading,
    inputConnectionChunks,
    outputConnectionChunks,
    setNodes,
    setEdges,
    setPersistentNodesState,
  ])

  const nodeTypes = useMemo(
    () => ({
      audience: AudienceNode,
      source: SourceNode,
      destination_account: DestinationNodeAccount,
      destination_campaign: DestinationNodeCampaign,
    }),
    [],
  )
  return (
    <ReactFlow
      nodes={nodes}
      edges={edges}
      preventScrolling={false}
      panOnDrag={false}
      zoomOnScroll={false}
      zoomOnPinch={false}
      draggable={false}
      fitView
      fitViewOptions={{
        padding: 0.5,
        maxZoom: 0.94,
      }}
      onNodesChange={onNodesChange}
      onEdgesChange={onEdgesChange}
      nodeTypes={nodeTypes}
      defaultEdgeOptions={defaultEdgeOptions}
      proOptions={{ hideAttribution: true }}
      className={'bg-white'}>
      <Background
        variant={BackgroundVariant.Lines}
        className={'bg-gradient-to-b from-white to-transparent to-white'}
      />
    </ReactFlow>
  )
}

const FlowWithProvider = (props: { connectors: any; isLoading: boolean }) => {
  return (
    <ReactFlowProvider>
      <AudienceFlowDiagram {...props} />
    </ReactFlowProvider>
  )
}

export default FlowWithProvider
